import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  InputAdornment,
  FormHelperText,
  Card,
  CardContent,
  Divider,
  Alert,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { GlobalContext } from "../../components/GlobalStateProvide/GlobalStateProvider";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import PaymentIcon from "@mui/icons-material/Payment";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import "./generatePaymentLinks.scss";

// Validation schema
const paymentLinkValidationSchema = yup.object().shape({
  amount: yup
    .number()
    .required("Amount is required")
    .positive("Amount must be greater than 0")
    .typeError("Amount must be a number"),
  customerName: yup
    .string()
    .trim()
    .required("Customer name is required")
    .matches(/^[A-Za-z\s]+$/, "Name should only contain alphabets and spaces"),
  customerEmail: yup
    .string()
    .email("Invalid email format")
    .matches(
      /^[a-zA-Z0-9._\-+]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
      "Invalid email format"
    )
    .required("Email is required"),
  customerPhone: yup
    .string()
    .required("Phone number is required")
    .matches(/^[0-9]{10}$/, "Phone number must be exactly 10 digits"),
  planName: yup.string().trim().required("Plan name is required"),
  planType: yup.string().trim().required("Plan type is required"),
  paymentPurpose: yup.string().trim().required("Payment purpose is required"),
  description: yup.string().trim().optional(),
  expiryDays: yup
    .number()
    .required("Expiry days is required")
    .positive("Must be greater than 0")
    .integer("Must be a whole number")
    .min(1, "Minimum 1 day")
    .max(365, "Maximum 365 days")
    .typeError("Must be a number"),
  createdBy: yup
    .string()
    .trim()
    .required("Creator name is required")
    .matches(
      /^[A-Za-z\s]+$/,
      "Creator name should only contain alphabets and spaces"
    ),
});

const GeneratePaymentLinks = () => {
  const { handleSnackBar } = useContext(GlobalContext);
  const [generatedLink, setGeneratedLink] = useState("");
  const [loading, setLoading] = useState(false);

  // Plan types for dropdown
  const planTypes = [
    { value: "basic", label: "Basic" },
    { value: "standard", label: "Standard" },
    { value: "premium", label: "Premium" },
    { value: "enterprise", label: "Enterprise" },
  ];

  // Payment purposes for dropdown
  const paymentPurposes = [
    { value: "subscription", label: "Subscription" },
    { value: "job_posting", label: "Job Posting" },
    { value: "featured_listing", label: "Featured Listing" },
    { value: "premium_services", label: "Premium Services" },
    { value: "hiring_credits", label: "Hiring Credits" },
    { value: "other", label: "Other" },
  ];

  const initialValues = {
    amount: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    planName: "",
    planType: "",
    paymentPurpose: "",
    description: "",
    expiryDays: 7,
    createdBy: "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    try {
      // TODO: Replace with actual API call
      // const response = await postRequestWithToken('admin/payment/generate-link', values);

      // Simulated response for UI testing
      const mockLink = `https://payment.jobsyahan.com/pay/${Math.random()
        .toString(36)
        .substring(7)}`;

      setGeneratedLink(mockLink);
      handleSnackBar(true, "Payment link generated successfully!", "success");

      // Uncomment below when API is ready
      // setGeneratedLink(response?.data?.paymentLink);
      // handleSnackBar(true, response?.data?.message || "Payment link generated successfully!", "success");

    } catch (error) {
      handleSnackBar(
        true,
        error?.response?.data?.message || "Failed to generate payment link",
        "error"
      );
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      handleSnackBar(true, "Payment link copied to clipboard!", "success");
    }
  };

  const handleReset = (resetForm) => {
    resetForm();
    setGeneratedLink("");
  };

  return (
    <Box className="generate-payment-links-section">
      <Box className="header-section">
        <Typography variant="h4" className="page-title">
          <LinkIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Generate Payment Link
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Create secure payment links for customers
        </Typography>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={paymentLinkValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, resetForm }) => (
          <Form>
            <Grid container spacing={3}>
              {/* Main Form Card */}
              <Grid item xs={12} lg={8}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <PersonIcon sx={{ mr: 1 }} color="primary" />
                      Customer Details
                    </Typography>

                    <Grid container spacing={2}>
                      {/* Customer Name */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Customer Name"
                          name="customerName"
                          value={values.customerName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.customerName && Boolean(errors.customerName)}
                          helperText={touched.customerName && errors.customerName}
                          placeholder="Enter customer name"
                          required
                        />
                      </Grid>

                      {/* Customer Email */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Customer Email"
                          name="customerEmail"
                          type="email"
                          value={values.customerEmail}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.customerEmail && Boolean(errors.customerEmail)}
                          helperText={touched.customerEmail && errors.customerEmail}
                          placeholder="customer@example.com"
                          required
                        />
                      </Grid>

                      {/* Customer Phone */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Customer Phone"
                          name="customerPhone"
                          value={values.customerPhone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.customerPhone && Boolean(errors.customerPhone)}
                          helperText={touched.customerPhone && errors.customerPhone}
                          placeholder="10-digit mobile number"
                          required
                          InputProps={{
                            startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                          }}
                        />
                      </Grid>

                      {/* Created By */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Created By"
                          name="createdBy"
                          value={values.createdBy}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.createdBy && Boolean(errors.createdBy)}
                          helperText={touched.createdBy && errors.createdBy}
                          placeholder="Admin/Staff name"
                          required
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <PaymentIcon sx={{ mr: 1 }} color="primary" />
                      Payment Details
                    </Typography>

                    <Grid container spacing={2}>
                      {/* Amount */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Amount"
                          name="amount"
                          type="number"
                          value={values.amount}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.amount && Boolean(errors.amount)}
                          helperText={touched.amount && errors.amount}
                          placeholder="0.00"
                          required
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </Grid>

                      {/* Plan Type */}
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          fullWidth
                          error={touched.planType && Boolean(errors.planType)}
                          required
                        >
                          <InputLabel>Plan Type</InputLabel>
                          <Select
                            name="planType"
                            value={values.planType}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            label="Plan Type"
                          >
                            <MenuItem value="">
                              <em>Select plan type</em>
                            </MenuItem>
                            {planTypes.map((plan) => (
                              <MenuItem key={plan.value} value={plan.value}>
                                {plan.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.planType && errors.planType && (
                            <FormHelperText>{errors.planType}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      {/* Plan Name */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Plan Name"
                          name="planName"
                          value={values.planName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.planName && Boolean(errors.planName)}
                          helperText={touched.planName && errors.planName}
                          placeholder="e.g., 3 Month Premium Plan"
                          required
                        />
                      </Grid>

                      {/* Payment Purpose */}
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          fullWidth
                          error={touched.paymentPurpose && Boolean(errors.paymentPurpose)}
                          required
                        >
                          <InputLabel>Payment Purpose</InputLabel>
                          <Select
                            name="paymentPurpose"
                            value={values.paymentPurpose}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            label="Payment Purpose"
                          >
                            <MenuItem value="">
                              <em>Select payment purpose</em>
                            </MenuItem>
                            {paymentPurposes.map((purpose) => (
                              <MenuItem key={purpose.value} value={purpose.value}>
                                {purpose.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.paymentPurpose && errors.paymentPurpose && (
                            <FormHelperText>{errors.paymentPurpose}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      {/* Expiry Days */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Link Expiry (Days)"
                          name="expiryDays"
                          type="number"
                          value={values.expiryDays}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.expiryDays && Boolean(errors.expiryDays)}
                          helperText={touched.expiryDays && errors.expiryDays || "Link will expire after specified days"}
                          placeholder="7"
                          required
                          InputProps={{
                            inputProps: { min: 1, max: 365 }
                          }}
                        />
                      </Grid>

                      {/* Description */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Description (Optional)"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.description && Boolean(errors.description)}
                          helperText={touched.description && errors.description}
                          placeholder="Add any additional notes or description"
                          multiline
                          rows={3}
                        />
                      </Grid>
                    </Grid>

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", gap: 2, mt: 4, justifyContent: "flex-end" }}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleReset(resetForm)}
                        disabled={isSubmitting || loading}
                      >
                        Reset
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || loading}
                        startIcon={<LinkIcon />}
                      >
                        {loading ? "Generating..." : "Generate Payment Link"}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Preview/Info Card */}
              <Grid item xs={12} lg={4}>
                <Card elevation={2} sx={{ position: "sticky", top: 20 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                      <DescriptionIcon sx={{ mr: 1 }} color="primary" />
                      Summary
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {values.amount || values.planName || values.customerName ? (
                      <Box className="summary-details">
                        {values.customerName && (
                          <Box className="summary-item">
                            <Typography variant="caption" color="textSecondary">
                              Customer
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {values.customerName}
                            </Typography>
                          </Box>
                        )}

                        {values.planName && (
                          <Box className="summary-item">
                            <Typography variant="caption" color="textSecondary">
                              Plan
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {values.planName}
                            </Typography>
                          </Box>
                        )}

                        {values.amount && (
                          <Box className="summary-item">
                            <Typography variant="caption" color="textSecondary">
                              Amount
                            </Typography>
                            <Typography variant="h6" color="primary" fontWeight={700}>
                              ₹ {values.amount}
                            </Typography>
                          </Box>
                        )}

                        {values.expiryDays && (
                          <Box className="summary-item">
                            <Typography variant="caption" color="textSecondary">
                              Expires In
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {values.expiryDays} day(s)
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Fill the form to see payment summary
                      </Alert>
                    )}

                    {generatedLink && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Alert severity="success" sx={{ mb: 2 }}>
                          Payment link generated successfully!
                        </Alert>
                        <Box className="generated-link-section">
                          <Typography variant="caption" color="textSecondary" gutterBottom>
                            Generated Payment Link
                          </Typography>
                          <Paper
                            elevation={1}
                            sx={{
                              p: 1.5,
                              bgcolor: "grey.100",
                              wordBreak: "break-all",
                              mt: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                              {generatedLink}
                            </Typography>
                          </Paper>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopyLink}
                            sx={{ mt: 2 }}
                          >
                            Copy Link
                          </Button>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default GeneratePaymentLinks;
